import React, { useState, useEffect } from 'react';
import {
  Search, Trash2, Calendar, DollarSign, User,
  ChevronLeft, ChevronRight, AlertTriangle, X, Repeat, Filter
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState(''); // '', 'monthly', 'yearly'
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchPayments(1, searchTerm, filterType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const fetchPayments = async (page = 1, search = '', type = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('mstechs_token');
      let url = `${API_URL}/api/payments/cards?page=${page}&limit=12`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (type) url += `&type=${type}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPayments(data.payments);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPayments(1, searchTerm, filterType);
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const response = await fetch(`${API_URL}/api/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setDeleteConfirm(null);
        fetchPayments(pagination.current, searchTerm, filterType);
      } else {
        alert(data.message);
      }
    } catch (err) { console.error(err); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('sr-RS').format(amount) + ' RSD';
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('sr-RS');

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Ucitavanje...</p>
      </div>
    );
  }

  return (
    <div className="payments-page">
      <div className="page-header">
        <div className="header-actions">
          <form onSubmit={handleSearch} className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Pretrazi po imenu klijenta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>
          <div className="filter-group">
            <Filter size={16} />
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">Svi tipovi</option>
              <option value="monthly">Mesecne</option>
              <option value="yearly">Godisnje</option>
            </select>
          </div>
          <div className="payments-total-badge">
            <DollarSign size={18} />
            <span>Ukupno uplata: {pagination.total}</span>
          </div>
        </div>
      </div>

      <div className="payments-grid">
        {payments.length > 0 ? (
          payments.map((payment) => (
            <div key={payment._id} className={`payment-card payment-card-${payment.type}`}>
              <div className="payment-card-header">
                <div className="payment-client">
                  <User size={18} />
                  <span>{payment.clientName}</span>
                </div>
                <span className={`type-badge ${payment.type}`}>
                  {payment.type === 'monthly' ? <><Repeat size={11} /> M</> : <><Calendar size={11} /> G</>}
                </span>
              </div>

              <div className="payment-card-amount">
                {formatCurrency(payment.amount)}
              </div>

              <div className="payment-card-details">
                <div className="payment-detail">
                  <Calendar size={14} />
                  <span>Datum: {formatDate(payment.paymentDate)}</span>
                </div>
                <div className="payment-detail">
                  <span className="period-label">Period:</span>
                  <span>{formatDate(payment.periodStart)} - {formatDate(payment.periodEnd)}</span>
                </div>
              </div>

              <div className="payment-card-footer">
                {payment.canDelete ? (
                  <button
                    className="btn-icon delete"
                    onClick={() => setDeleteConfirm(payment)}
                    title="Obrisi uplatu"
                  >
                    <Trash2 size={16} />
                  </button>
                ) : (
                  <div className="payment-card-badge">
                    <span>Postoji novija uplata istog tipa</span>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="empty-payments">
            <DollarSign size={48} />
            <p>Nema pronadjenih uplata</p>
          </div>
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => fetchPayments(pagination.current - 1, searchTerm, filterType)}
            disabled={pagination.current === 1}
            className="pagination-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="pagination-info">
            Strana {pagination.current} od {pagination.pages}
          </span>
          <button
            onClick={() => fetchPayments(pagination.current + 1, searchTerm, filterType)}
            disabled={pagination.current === pagination.pages}
            className="pagination-btn"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Potvrda brisanja</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Obrisati <strong>{deleteConfirm.type === 'monthly' ? 'mesecnu' : 'godisnju'}</strong> uplatu od <strong>{formatCurrency(deleteConfirm.amount)}</strong> za klijenta <strong>{deleteConfirm.clientName}</strong>?
              </p>
              <p className="warning-text">
                <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Datum naplate ce biti vracen {deleteConfirm.type === 'monthly' ? 'mesec' : 'godinu'} unazad.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Otkazi</button>
              <button className="btn-delete" onClick={() => handleDeletePayment(deleteConfirm._id)}>Obrisi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;